import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Column } from 'react-table'
import Table from '../../../global/components/table/Table'
import BasicFilter from '../../../global/components/table/helpers/filters/BasicFilter'
import DateFilter from '../../../global/components/table/helpers/filters/DateFilter'
import DropdownFilter from '../../../global/components/table/helpers/filters/DropdownFilter'
import LocationFilter from '../../../global/components/table/helpers/filters/LocationFilter'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import retrieveData from '../../../global/components/table/helpers/retrieveData'
import { IOrder } from '../../../../../app/entities/Order'
import { ILocationFilters } from '../main/QueuesMain'
import WorkOrderDetailModal from '../../../work-orders/components/work-order-detail/components/main/WorkOrderDetailModal'
import { WoDetailContext } from '../../../work-orders/components/context/context'
import removeDoubleCommas from '../../../global/utils/removeDoubleCommas'
import useFetch from '../../../global/hooks/useFetchHook'
import { assignUserToOrder, getRescheduleQueueAssignToUsersList } from '../api'
import { IUser } from '../../../../../app/entities/User'
import AssignUserCell from '../../../global/components/assign-user/AssignUserCell'
import hasPermission from '../../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../../global/constants/permissions'
import { UserContext } from '../../../base/components/base-container/BaseContainer'
import { TIME_SLOT_TYPES } from '../../../global/constants/time-slot'

interface IWorkOrdersTableProps {
  locationFilters: ILocationFilters
}

function capitalizeStatus(status: string): string {
  return status
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function formatDate(dateValue: string): string {
  if (!dateValue || dateValue.length === 0) return ''
  const [year, month, day] = dateValue.split('-')
  return `${month}/${day}/${year.slice(-2)}`
}

function RescheduleQueueTable(props: IWorkOrdersTableProps) {
  const { user } = useContext(UserContext)
  const { id } = useParams()

  const { data: usersList } = useFetch<IUser[]>(
    getRescheduleQueueAssignToUsersList(),
  )
  const { locationFilter } = props.locationFilters
  const { queryOrderData, clearData } = useContext(WoDetailContext)
  const navigate = useNavigate()
  const [detailsModalOpen, setDetailsModalOpen] = useState<boolean>(false)
  const [tableState, setTableState] = useState({
    data: [],
    isLoading: false,
    count: 0,
    pageSize: 100,
    offSet: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})
  const [locationColumnFilter, setLocationColumnFilter] = useState('')
  const [serviceApptFilter, setServiceApptFilter] = useState('')
  const [timeSlotFilter, setTimeSlotFilter] = useState('')

  async function handleDetailsOpen(orderObjectId: string) {
    setDetailsModalOpen(true)

    const url = `/queues/reschedule/${orderObjectId}`
    navigate(url)
    await queryOrderData(orderObjectId)
  }

  async function handleDetailsClosed() {
    setDetailsModalOpen(false)
    navigate(`/queues`)

    clearData()
    retrieveTableData()
  }

  function onRecordClickHandler(row: any) {
    const {
      original: { objectId: orderObjectId },
    } = row
    handleDetailsOpen(orderObjectId)
  }

  const onColumnFilterUpdate = (filtersUpdate: object) => {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  useEffect(() => {
    if (id && !detailsModalOpen) {
      handleDetailsOpen(id)
    }
  }, [id])

  const endpoint = `${process.env.REACT_APP_ORIGIN}/orders/pending-rescheduling?`
  function retrieveTableData() {
    retrieveData(setTableState, {
      endpoint,
      pageSize: tableState.pageSize,
      offSet: tableState.offSet,
      sortOrder: 'DESC',
      selectedLocationId: locationFilter,
      columnFilters,
      dataTransformer: (data: any) => {
        data.items.forEach((item: any) => {
          if (item.installationPoint) {
            item.installationPoint.fullAddress = removeDoubleCommas(
              item.installationPoint?.fullAddress,
            )
          }
        })

        return data.items
      },
    })
  }

  useEffect(retrieveTableData, [
    columnFilters,
    locationFilter,
    tableState.pageSize,
    tableState.offSet,
  ])

  const columns: Column<IOrder | any>[] = [
    {
      Header: 'Assigned To',
      accessor: 'assignedTo',
      Filter: BasicFilter({
        onChange: (val: string) => onColumnFilterUpdate({ assignedTo: val }),
      }),
      Cell: ({ row: { original } }) => {
        const { assignedTo, objectId } = original as any
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <AssignUserCell
              assignedTo={assignedTo && assignedTo}
              usersList={usersList ?? []}
              objectId={objectId}
              retrieveTableData={retrieveTableData}
              disabled={
                !hasPermission(user).allow(
                  PERMISSIONS.QUEUES.CATEGORY,
                  PERMISSIONS.QUEUES.ENTRIES.RESCHEDULE_ENTRIES_ASSIGNER.NAME,
                )
              }
              assignUserTo={assignUserToOrder}
            />
          </div>
        )
      },
    },
    {
      Header: 'Order ID',
      accessor: 'workOrderId',
      Filter: BasicFilter({
        onChange: (val: string) => onColumnFilterUpdate({ workOrderId: val }),
      }),
    },
    {
      Header: 'TR Order ID',
      accessor: 'tireRackOrders.0.orderNumber',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackOrders: {
              orderNumber: val,
            },
          }),
      }),
    },
    {
      Header: 'Location',
      accessor: 'location.name',
      Filter: LocationFilter({
        value: locationColumnFilter,
        onChange: (val: string) => {
          setLocationColumnFilter(val)
          onColumnFilterUpdate({ location: { name: val } })
        },
      }),
    },
    {
      Header: 'Technician Name',
      accessor: 'routeArea.technician.name',
      Cell: ({ row: { original } }) => {
        const { routeArea, routeInstructions } = original as any

        if (routeInstructions.length) {
          const instruction = routeInstructions[0]
          const technicianName = instruction.route?.technician?.name
          if (technicianName) return <div>{technicianName}</div>
        }
        return <div>{routeArea?.technician?.name}</div>
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            routeArea: {
              technician: {
                name: val,
              },
            },
          }),
      }),
    },
    {
      Header: 'Service Appt',
      accessor: 'timeSlot.fromDateGenerated',
      Filter: DateFilter({
        value: serviceApptFilter,
        onChange: (val: string) => {
          setServiceApptFilter(val)
          onColumnFilterUpdate({
            timeSlot: {
              fromDateGenerated: formatDate(val),
            },
          })
        },
      }),
      sortInverted: true,
    },
    {
      Header: 'Time Slot',
      accessor: 'timeSlot.type',
      Cell: ({ row: { original } }) => {
        const { timeSlot } = original as IOrder
        return <div>{capitalizeStatus(timeSlot?.type || '')}</div>
      },
      Filter: DropdownFilter({
        value: timeSlotFilter,
        onChange: (val: string) => {
          setTimeSlotFilter(val)
          onColumnFilterUpdate({
            timeSlot: {
              type: val,
            },
          })
        },
        items: Object.values(TIME_SLOT_TYPES).map((type) => ({
          key: type,
          value: capitalizeStatus(type),
        })),
      }),
    },
    {
      Header: 'First Name',
      accessor: 'customer.firstName',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { firstName: val } }),
      }),
    },
    {
      Header: 'Last Name',
      accessor: 'customer.lastName',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { lastName: val } }),
      }),
    },
    {
      Header: 'Customer Email',
      accessor: 'customer.email',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { email: val } }),
      }),
    },
    {
      Header: 'Customer Phone',
      accessor: 'customer.trimmedPhoneNumber',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { trimmedPhoneNumber: val } }),
      }),
    },
    {
      Header: 'Installation Address',
      accessor: 'installationPoint.fullAddress',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ installationPoint: { fullAddress: val } }),
      }),
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <WorkOrderDetailModal
        handleClosed={handleDetailsClosed}
        isOpen={detailsModalOpen}
      />
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={onRecordClickHandler}
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default RescheduleQueueTable
